import React from "react";
import { useNavigate } from "react-router-dom";
import { ModuleI, ModuleStatus } from "../../../types";
import { usePermissions } from "../../../contexts/PermissionContext";
import { analytics } from "../../../services/AnalyticsService";
import { getModuleStatusSolidBgClass } from "../../ContentManagementPage/components/ContentTopicsList";
import PflLogo from "../../../assets/tutorial/PfL-logo-3pt-2025.svg";

interface TopicCardProps {
  module: ModuleI;
  /** When true, shows the same status circle as Content Management (draft / published / etc.). */
  showStatusIndicator?: boolean;
}

export default function TopicCard({
  module,
  showStatusIndicator = false,
}: TopicCardProps) {
  const navigate = useNavigate();
  const { canReadContent } = usePermissions();
  const isDisabled =
    module.status === ModuleStatus.Coming_Soon && !canReadContent;
  const thumbnail =
    module.thumbnail && typeof module.thumbnail !== "string"
      ? module.thumbnail
      : null;

  const handleClick = () => {
    if (isDisabled) return;
    navigate(`/topics/${module.slug ?? module._id}`);
    analytics.page({
      category: "Module",
      elementId: module._id,
    });
  };

  const buttonClass = [
    "relative w-full text-left rounded-xl overflow-hidden shadow-md bg-white transition-transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-electrum-300",
    isDisabled ? "opacity-60 grayscale cursor-not-allowed" : "cursor-pointer",
  ].join(" ");

  const status = module.status;

  return (
    <button onClick={handleClick} disabled={isDisabled} className={buttonClass}>
      {/* Thumbnail area */}
      <div className="w-full aspect-[20/10] bg-electrum-50 flex items-center justify-center overflow-hidden">
        {thumbnail ? (
          <img
            src={thumbnail.src}
            alt={thumbnail.alt || `${module.title} thumbnail`}
            className="w-full h-full object-cover"
          />
        ) : (
          <img
            src={PflLogo}
            alt={`${module.title} thumbnail`}
            className="w-20 h-20 object-contain"
          />
        )}
      </div>

      {/* Info area */}
      <div className="flex items-center gap-3 p-3">
        {typeof module.icon !== "string" && (
          <img
            className="w-10 h-10 flex-shrink-0"
            src={module.icon.src}
            alt={module.icon.alt}
          />
        )}
        <div className="min-w-0">
          <div className="font-bold text-base truncate">{module.title}</div>
          {module.subtitle && (
            <div className="text-sm text-electrum truncate">
              {module.subtitle}
            </div>
          )}
        </div>
      </div>
      {showStatusIndicator && status && (
        <span
          className={`pointer-events-none absolute bottom-2 right-2 flex h-6 w-6 items-center justify-center rounded-full text-sm font-bold text-white shadow-md ring-2 ring-white ${getModuleStatusSolidBgClass(
            status,
          )}`}
          title={status}
          aria-label={`Status: ${status}`}
        >
          {status.charAt(0).toUpperCase()}
        </span>
      )}
    </button>
  );
}
